import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders its children at the end of the document rather than where they sit
 * in the tree.
 *
 * Anything that has to float above the whole page needs this. A dashboard
 * card is `position: absolute` with a `z-index`, which makes it a stacking
 * context — and a dialog rendered inside one can only ever stack against that
 * card's own children, never against the card next to it. No z-index is high
 * enough to escape; a portal is the only way out.
 */
export function Portal({ children }: { children: ReactNode }) {
  // Mounted state, so nothing tries to reach for `document` before it exists.
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  if (!ready) return null;
  return createPortal(children, document.body);
}
